import { SyntheticEvent, useCallback, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { TextFieldProps, fieldToTextField } from 'formik-mui';
import { IManager } from '@sr/dto';
import { useReadOnlyRemoteCollection } from 'utils/remote-collection.hook';
import { fetchCollectionReq } from 'shared/api/axios';
import { mergeAutocompleteInputSx } from 'shared/ui/autocomplete-input-sx';

const fetchManagersDropdown = async () => {
  return fetchCollectionReq<IManager>('tasks/manager-dropdown');
};

type ManagerPickerFieldProps = {
  clearable?: boolean;
  size?: 'small' | 'medium' | undefined;
} & TextFieldProps;

export const ManagerPickerField = (props: ManagerPickerFieldProps) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    size = undefined,
  } = props;

  const { items: managers, isLoading: fetching } = useReadOnlyRemoteCollection<IManager>({
    fetchHandler: useCallback(() => fetchManagersDropdown(), []),
  });

  const selectedManager = useMemo(() => managers.find((m) => m.id === value) || null, [managers, value]);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: IManager | null) => {
      if (!newValue && !props.clearable) return;
      setFieldValue(name, newValue ? newValue.id : null);
    },
    [props.clearable, setFieldValue, name],
  );

  return (
    <Autocomplete
      fullWidth
      value={selectedManager}
      options={managers}
      disabled={props.disabled}
      disableClearable={!props.clearable}
      size={size}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => !value || option.id === value.id}
      getOptionLabel={(option) => `${option.firstName} ${option.secondName || ''}`.trim()}
      loading={fetching}
      loadingText={'Загрузка...'}
      noOptionsText={'Ничего не найдено'}
      renderInput={(params) => {
        const { sx, ...restFieldProps } = fieldToTextField(props);
        return (
          <TextField
            {...restFieldProps}
            name={name}
            {...params}
            disabled={props.disabled}
            label={props.label ?? 'Руководитель'}
            placeholder="Начните набирать имя..."
            sx={mergeAutocompleteInputSx(sx)}
          />
        );
      }}
    />
  );
};
